import { type CSSProperties, type JSX, useEffect, useState } from 'react';

export interface HeroWeatherProps {
  /** Whether to show the date line. Defaults true. */
  showDate?: boolean;
  /** Fixed latitude. If absent, attempts browser geolocation. */
  latitude?: number;
  /** Fixed longitude. */
  longitude?: number;
  /** Human-readable place name shown under the weather card. */
  locationLabel?: string;
  /** Optional hero background image (Pro). */
  imageSrc?: string;
}

interface WeatherSample {
  temperatureF: number;
  windMph: number;
  weatherCode: number;
}

/** Open-Meteo response shape (subset we use). */
interface OpenMeteoResponse {
  current?: {
    temperature_2m?: number;
    wind_speed_10m?: number;
    weather_code?: number;
  };
}

/**
 * In-memory cache of recent fetches, keyed by lat/lng rounded to 2
 * decimals (~1 km granularity — plenty for kiosk locale). Bounded TTL
 * because weather changes; bounded SIZE isn't a concern here since a
 * single kiosk has one config and thus one coord pair.
 */
const WEATHER_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
const weatherCache = new Map<string, { sample: WeatherSample; ts: number }>();

/**
 * Weather + clock hero. Uses Open-Meteo (free, no API key, no
 * signup): https://open-meteo.com/. If lat/lng aren't in config,
 * tries browser geolocation; if THAT's denied/unavailable, renders
 * the clock alone with no weather card.
 */
export function HeroWeather({
  showDate = true,
  latitude,
  longitude,
  locationLabel,
  imageSrc,
}: HeroWeatherProps): JSX.Element {
  const [now, setNow] = useState<Date>(() => new Date());
  const [sample, setSample] = useState<WeatherSample | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ok' | 'failed' | 'denied'>('idle');

  // Clock tick — same 15s cadence as HeroClock.
  useEffect(() => {
    const handle = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(handle);
  }, []);

  // Weather fetch — resolves coords (config → geolocation), checks
  // cache, then hits Open-Meteo. Re-runs on coord changes only; the
  // sample lives for WEATHER_CACHE_TTL_MS regardless of re-renders.
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    (async () => {
      const coords = await resolveCoords(latitude, longitude);
      if (cancelled) return;
      if (coords === 'denied') {
        setStatus('denied');
        return;
      }
      if (coords === null) {
        setStatus('failed');
        return;
      }

      const key = `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}`;
      const cached = weatherCache.get(key);
      if (cached !== undefined && Date.now() - cached.ts < WEATHER_CACHE_TTL_MS) {
        setSample(cached.sample);
        setStatus('ok');
        return;
      }

      try {
        const url =
          `https://api.open-meteo.com/v1/forecast?latitude=${coords.lat}` +
          `&longitude=${coords.lng}&current=temperature_2m,wind_speed_10m,weather_code` +
          `&temperature_unit=fahrenheit&wind_speed_unit=mph`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`open-meteo ${String(res.status)}`);
        const data = (await res.json()) as OpenMeteoResponse;
        const cur = data.current;
        if (
          cur === undefined ||
          typeof cur.temperature_2m !== 'number' ||
          typeof cur.weather_code !== 'number'
        ) {
          throw new Error('open-meteo missing fields');
        }
        const next: WeatherSample = {
          temperatureF: Math.round(cur.temperature_2m),
          windMph: typeof cur.wind_speed_10m === 'number' ? Math.round(cur.wind_speed_10m) : 0,
          weatherCode: cur.weather_code,
        };
        weatherCache.set(key, { sample: next, ts: Date.now() });
        if (!cancelled) {
          setSample(next);
          setStatus('ok');
        }
      } catch {
        if (!cancelled) setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [latitude, longitude]);

  const style: CSSProperties | undefined =
    imageSrc !== undefined
      ? {
          backgroundImage:
            `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.45) 100%), url(${imageSrc})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }
      : undefined;

  return (
    <section
      className={`launcher-hero launcher-hero--weather${
        imageSrc !== undefined ? ' launcher-hero--has-image' : ''
      }`}
      {...(style && { style })}
      data-testid="launcher-hero"
      data-hero-kind="weather"
    >
      <div className="launcher-hero__weather-stack">
        <p className="launcher-hero__clock-time" data-testid="launcher-hero-time" aria-live="polite">
          {formatTime(now)}
        </p>
        {showDate && (
          <p className="launcher-hero__clock-date" data-testid="launcher-hero-date">
            {formatDate(now)}
          </p>
        )}
        {status === 'ok' && sample !== null && (
          <div className="launcher-hero__weather-card" data-testid="launcher-hero-weather">
            <span className="launcher-hero__weather-icon" aria-hidden>
              {weatherCodeIcon(sample.weatherCode)}
            </span>
            <span className="launcher-hero__weather-temp">{sample.temperatureF}°F</span>
            <span className="launcher-hero__weather-desc">
              {weatherCodeLabel(sample.weatherCode)}
              {sample.windMph > 0 ? ` · ${String(sample.windMph)} mph wind` : ''}
            </span>
            {locationLabel !== undefined && locationLabel.length > 0 && (
              <span className="launcher-hero__weather-location">{locationLabel}</span>
            )}
          </div>
        )}
        {(status === 'loading' || status === 'idle') && (
          <p className="launcher-hero__weather-hint">Loading weather…</p>
        )}
        {(status === 'denied' || status === 'failed') && (
          <p className="launcher-hero__weather-hint">
            {status === 'denied' ? 'Location access required for weather' : 'Weather unavailable'}
          </p>
        )}
      </div>
    </section>
  );
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function formatDate(d: Date): string {
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

/**
 * Resolves coords from config, falling back to browser geolocation. Returns:
 *   { lat, lng }   on success
 *   'denied'       when the user blocked geolocation
 *   null           on any other failure (no geolocation API, timeout, etc.)
 */
async function resolveCoords(
  configLat: number | undefined,
  configLng: number | undefined,
): Promise<{ lat: number; lng: number } | 'denied' | null> {
  if (configLat !== undefined && configLng !== undefined) {
    return { lat: configLat, lng: configLng };
  }
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
    return null;
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) resolve('denied');
        else resolve(null);
      },
      { timeout: 8_000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

/**
 * Open-Meteo weather codes — WMO 4677. Minimal mapping; collapses dozens
 * of sub-codes into the eight buckets most fleets care about.
 * https://open-meteo.com/en/docs#weathervariables
 */
function weatherCodeLabel(code: number): string {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly cloudy';
  if (code <= 48) return 'Fog';
  if (code <= 57) return 'Drizzle';
  if (code <= 67) return 'Rain';
  if (code <= 77) return 'Snow';
  if (code <= 82) return 'Showers';
  if (code <= 86) return 'Snow showers';
  return 'Thunderstorm';
}
function weatherCodeIcon(code: number): string {
  if (code === 0) return '☀';
  if (code <= 3) return '⛅';
  if (code <= 48) return '🌫';
  if (code <= 67) return '🌧';
  if (code <= 77) return '❄';
  if (code <= 82) return '🌦';
  if (code <= 86) return '🌨';
  return '⛈';
}

/**
 * @internal — exported for tests to seed/clear the cache between cases.
 */
export const _weatherCache = weatherCache;
